/**
 * AuditsList Page
 * List of all healing audit records
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Tooltip,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import {
  AuditOutlined,
  SearchOutlined,

  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  RollbackOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import LoadingSpinner from '../components/LoadingSpinner';
import { auditApi } from '../api/hcApi';
import type { Audit, AuditStatus } from '../types';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const statusConfig: Record<
  AuditStatus,
  { color: string; icon: React.ReactNode }
> = {
  pending: { color: 'default', icon: <ClockCircleOutlined /> },
  in_progress: { color: 'processing', icon: <SyncOutlined spin /> },
  completed: { color: 'success', icon: <CheckCircleOutlined /> },
  failed: { color: 'error', icon: <CloseCircleOutlined /> },
};

const actionColors: Record<string, string> = {
  restart: 'orange',
  scale: 'blue',
  clear_cache: 'cyan',
  notify: 'purple',
  custom: 'magenta',
};

const AuditsList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [filteredAudits, setFilteredAudits] = useState<Audit[]>([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [actionFilter, setActionFilter] = useState<string | undefined>();
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchAudits = async () => {
    setLoading(true);
    try {
      const response = await auditApi.list({
        page: pagination.current || 1,
        limit: pagination.pageSize || 10,
        status: statusFilter as AuditStatus | undefined,
      });
      if (response.success && response.data) {
        const auditData = Array.isArray(response.data) ? response.data : [];
        setAudits(auditData);
        setFilteredAudits(auditData);
        setPagination((prev) => ({ ...prev, total: auditData.length }));
      } else {
        setAudits([]);
        setFilteredAudits([]);
      }
    } catch (err) {
      console.warn('Failed to fetch audits:', err);
      setAudits([]);
      setFilteredAudits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudits();
  }, []);

  useEffect(() => {
    let filtered = [...audits];

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.id.toLowerCase().includes(search) ||
          a.policy_name.toLowerCase().includes(search) ||
          a.result?.message?.toLowerCase().includes(search)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((a) => a.status === statusFilter);
    }

    if (actionFilter) {
      filtered = filtered.filter((a) => a.action === actionFilter);
    }

    setFilteredAudits(filtered);
    setPagination((prev) => ({ ...prev, total: filtered.length }));
  }, [searchText, statusFilter, actionFilter, audits]);

  const handleRollback = (audit: Audit) => {
    message.loading('Rolling back action...', 1.5).then(() => {
      message.success(`Rollback initiated for ${audit.id}`);
    });
  };

  const handleViewDetails = (audit: Audit) => {
    navigate(`/audits/${audit.id}`);
  };

  const columns: ColumnsType<Audit> = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: AuditStatus) => {
        const config = statusConfig[status];
        return (
          <Tag icon={config.icon} color={config.color}>
            {status.replace('_', ' ').toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Policy',
      dataIndex: 'policy_name',
      key: 'policy_name',
      ellipsis: true,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (action: string) => (
        <Tag color={actionColors[action] || 'default'}>
          {action.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      ellipsis: true,
      render: (result: Audit['result']) => (
        <Text
          type={result?.success ? 'success' : result?.success === false ? 'danger' : 'secondary'}
        >
          {result?.message || 'Pending...'}
        </Text>
      ),
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 160,
      render: (ts: string) => dayjs(ts).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 100,
      render: (_, record) => {
        if (!record.completed_at) return '-';
        const duration =
          new Date(record.completed_at).getTime() -
          new Date(record.started_at).getTime();
        return `${(duration / 1000).toFixed(1)}s`;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {record.result?.rollback_available && (
            <Tooltip title="Rollback">
              <Button
                type="text"
                size="small"
                danger
                icon={<RollbackOutlined />}
                onClick={() => handleRollback(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const stats = {
    total: audits.length,
    completed: audits.filter((a) => a.status === 'completed').length,
    failed: audits.filter((a) => a.status === 'failed').length,
    pending: audits.filter((a) => a.status === 'pending' || a.status === 'in_progress').length,
    successRate:
      audits.filter((a) => a.result?.success).length /
      audits.filter((a) => a.status === 'completed' || a.status === 'failed').length || 0,
  };

  if (loading) {
    return <LoadingSpinner tip="Loading audits..." />;
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <AuditOutlined style={{ marginRight: 12 }} />
          Healing Audits
        </Title>
        <Text type="secondary">
          Track and manage automated healing actions
        </Text>
      </div>

      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Total Audits" value={stats.total} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Completed"
              value={stats.completed}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Failed"
              value={stats.failed}
              valueStyle={{ color: '#f5222d' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={(stats.successRate * 100).toFixed(1)}
              suffix="%"
              valueStyle={{ color: stats.successRate >= 0.9 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Search audits..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={4}>
            <Select
              placeholder="Status"
              style={{ width: '100%' }}
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'pending', label: 'Pending' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'completed', label: 'Completed' },
                { value: 'failed', label: 'Failed' },
              ]}
            />
          </Col>
          <Col xs={12} sm={4}>
            <Select
              placeholder="Action"
              style={{ width: '100%' }}
              allowClear
              value={actionFilter}
              onChange={setActionFilter}
              options={[
                { value: 'restart', label: 'Restart' },
                { value: 'scale', label: 'Scale' },
                { value: 'clear_cache', label: 'Clear Cache' },
                { value: 'notify', label: 'Notify' },
                { value: 'custom', label: 'Custom' },
              ]}
            />
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={2}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setSearchText('');
                setStatusFilter(undefined);
                setActionFilter(undefined);
              }}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Audits Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredAudits}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} audits`,
          }}
          onChange={(pag) => setPagination(pag)}
        />
      </Card>
    </div>
  );
};

export default AuditsList;
